import React, { useState, useEffect, useCallback, useContext } from "react";
import { Box, Chip, IconButton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth, useTablePagination } from "hooks";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useLazyListShipmentQuery } from "store/services/shipmentApi";
import { BaseTabs } from "components/Shared/BaseTabs";
import HighlightedText from "components/Shared/HighlightedText";
import { WarehouseDetailContext } from "contexts/WarehouseDetailContext";

const getStatusStyle = (status) => {
  const styles = {
    Warehousing: { backgroundColor: "#E5E7EB", textColor: "#4B5563" },
  };
  return styles[status] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const ShipmentInWarehouseList = () => {
  const { isVendor, vendorId, isCustomer } = useAuth();
  const { warehouseDetail } = useContext(WarehouseDetailContext); // Get warehouseDetail from context
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [fetchError, setFetchError] = useState(null);

  const shipmentTabs = [{ label: "Shipments", value: "Warehousing" }];

  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentId,
      cell: (row) => {
        const displayId = `${row.shipmentId}${row.IsReturning ? " (R)" : ""}`;
        const path = `/app/${
          isVendor ? "vendor" : isCustomer ? "customer" : "crm"
        }/shipment/${row._id}/detail`;
        return (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {displayId ? (
              <HighlightedText text={displayId} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </Link>
        );
      },
    },
    {
      name: "Sender",
      selector: (row) => row?.customerUser?.name || row?.vendor?.ownerName || "N/A",
      cell: (row) => (
        <Box
          sx={{
            maxWidth: "600px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row?.customerUser?.name
            ? `CUS: ${row.customerUser.name}`
            : row?.vendor?.ownerName
            ? `VEN: ${row.vendor.ownerName}`
            : "N/A"}
        </Box>
      ),
      omit: isVendor || isCustomer,
    },
    {
      name: "Receiver",
      selector: (row) => row?.receiver?.customerUser?.name,
      cell: (row) => (
        <div>
          <span className="text-sm">{row?.receiver?.customerUser?.name}</span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.receiver?.phone}
          </span>
        </div>
      ),
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch?.name,
      cell: (row) => row?.sourceBranch?.name,
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch?.name,
      cell: (row) => row?.destinationBranch?.name,
    },
    {
      name: "Pickup Time",
      selector: (row) => <AppTableDateTime date={row?.shipmentPickupTime} />,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Chip
          label={row?.status?.toUpperCase() || "N/A"}
          variant="outlined"
          sx={{
            backgroundColor: getStatusStyle(row?.status).backgroundColor,
            color: getStatusStyle(row?.status).textColor,
            fontWeight: "bold",
            height: "17px",
            borderRadius: "12px",
            width: "auto",
            padding: "0",
            border: "none",
            "& .MuiChip-label": {
              fontSize: "0.6rem",
              padding: "3px 5px",
              lineHeight: "14px",
            },
          }}
        />
      ),
    },
    {
      name: "Actions",
      cell: (row) => {
        const basePath = isVendor
          ? "/app/vendor/shipment"
          : isCustomer
          ? "/app/customer/shipment"
          : "/app/crm/shipment";
        return (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Link to={`${basePath}/${row._id}/detail`}>
              <IconButton
                size="small"
                aria-label="View Shipment"
                color="primary"
              >
                <InfoOutlined />
              </IconButton>
            </Link>
          </Box>
        );
      },
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchShipment, { data: shipmentRes, isLoading, isFetching }] =
    useLazyListShipmentQuery();

  const fetchShipments = useCallback(() => {
    if (!warehouseDetail?._id) {
      setFetchError("Warehouse ID is missing");
      return;
    }
    setFetchError(null);
    fetchShipment({
      status: "Warehousing",
      warehouse_id: warehouseDetail._id, 
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(isVendor && { vendorId }),
      sortedBy: "createdAt",
      sortDirection: -1,
    }).then((response) => {
      });
  }, [fetchShipment, currentPage, perPage, keyword, isVendor, vendorId, warehouseDetail?._id]);

  useEffect(() => {
    if (warehouseDetail?._id) {
      fetchShipments();
    }
  }, [fetchShipments, warehouseDetail?._id]);

  return (
    <>
      {fetchError && (
        <Box sx={{ mb: 2, color: "error.main" }}>
          {fetchError}
        </Box>
      )}
      <BaseTabs tabs={shipmentTabs} defaultValue="Warehousing" />
      <AppTable
        columns={columns}
        data={shipmentRes?.data ?? []}
        progressPending={isLoading || isFetching}
        paginationServer
        paginationPerPage={perPage}
        paginationTotalRows={shipmentRes?.totalItems ?? 0}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
      />
    </>
  );
};

export default ShipmentInWarehouseList;